// Global
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Local
import clsx from 'clsx';
import styles from './Header.module.scss';
import { PureHeaderPrimaryLinkBar, PureHeaderSectionLink } from './types';
import { HeaderMegaMenuContainer } from './HeaderMegaMenuContainer';
import { HeaderSubMenu } from './HeaderMobile/HeaderSubMenu';
import useClickOutside from 'lib/hooks/useClickOutside';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import useDictionary from 'lib/hooks/useDictionary';
import { generateSubmenuId } from './Header.utils';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { LinkContext } from 'components/helpers/Atoms/Cta/CtaLink';
interface HeaderPrimaryLinkBarProps {
  primaryLinkBar: PureHeaderPrimaryLinkBar[];
  mobileNavOpen: boolean;
  setMobileNavOpen: (mobileNavOpen: boolean) => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  linkContext: LinkContext;
}

export const HeaderPrimaryLinkBar = (props: HeaderPrimaryLinkBarProps) => {
  const navRef = useRef<HTMLElement>(null);
  const firstInteractiveElementRef = useRef<HTMLButtonElement>(null);
  const { getDictionaryValue } = useDictionary();

  // Setup state for accordion behavior - track which accordion is open
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(null);

  // Setup state for submenu
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [selectedSectionLink, setSelectedSectionLink] = useState<PureHeaderSectionLink | null>(
    null
  );
  const [submenuTriggerId, setSubmenuTriggerId] = useState<string | null>(null);
  const [pendingFocusTriggerId, setPendingFocusTriggerId] = useState<string | null>(null);
  const submenuTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const isTablet = useIsTablet();

  const navElement = navRef.current as HTMLElement | null;
  const buttonElement = props.buttonRef.current as HTMLElement | null;

  useEffect(() => {
    if (!openSubMenu && props.mobileNavOpen && pendingFocusTriggerId) {
      submenuTriggerRefs.current.get(pendingFocusTriggerId)?.focus();
      setPendingFocusTriggerId(null);
    }
  }, [openSubMenu, pendingFocusTriggerId, props.mobileNavOpen]);

  const registerSubmenuTrigger = (triggerId: string, element: HTMLButtonElement | null) => {
    if (element) {
      submenuTriggerRefs.current.set(triggerId, element);
    } else {
      submenuTriggerRefs.current.delete(triggerId);
    }
  };

  const setOpenSubMenuEvent = (
    sectionLink?: PureHeaderSectionLink,
    triggerId?: string,
    shouldRestoreFocus = false
  ) => {
    if (sectionLink) {
      setSelectedSectionLink(sectionLink);
      setSubmenuTriggerId(triggerId || null);
      setOpenSubMenu(true);
    } else {
      setOpenSubMenu(false);
      setSelectedSectionLink(null);
      if (shouldRestoreFocus) {
        setPendingFocusTriggerId(submenuTriggerId);
      } else {
        setSubmenuTriggerId(null);
      }
    }
  };

  // Keep focus on the toggle button when mobile nav opens so its expanded
  // state is announced; Tab naturally moves focus into the revealed nav.
  useLayoutEffect(() => {
    if (!props.mobileNavOpen) {
      setOpenAccordionIndex(null);
      setOpenSubMenu(false);
      setSelectedSectionLink(null);
    }
  }, [props.mobileNavOpen]);

  // Watch for outside focus and outside clicks
  useEffect(() => {
    if (props.mobileNavOpen) {
      document.addEventListener('focusin', handleFocusChange);
      document.addEventListener('keydown', handleEscapeKeyPress);
    }

    return () => {
      // Cleanup Function
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('keydown', handleEscapeKeyPress);
    };
  });

  // Use click outside hook for nav only
  useClickOutside(navRef, props.mobileNavOpen, () => props.setMobileNavOpen(false));

  // Additional click handler to prevent closing when clicking the button
  useEffect(() => {
    const handleButtonClick = (event: MouseEvent) => {
      const buttonElement = props.buttonRef.current as HTMLElement | null;
      if (buttonElement && buttonElement.contains(event.target as Node)) {
        // Prevent the useClickOutside from closing the menu
        event.stopPropagation();
      }
    };

    if (props.mobileNavOpen) {
      document.addEventListener('mousedown', handleButtonClick, true); // Use capture phase
    }

    return () => {
      document.removeEventListener('mousedown', handleButtonClick, true);
    };
  }, [props.mobileNavOpen, props.buttonRef]);

  // If focus isn't in the nav anymore, close mobile nav
  function handleFocusChange(e: FocusEvent) {
    // Return early if focus has left the document (aka dev tools, etc)
    if (!document.hasFocus()) {
      return;
    }

    // Check if focus is still within the nav or has moved to the hamburger button
    const isFocusInNav = navElement && navElement.contains(e.target as Node);
    const isFocusOnButton = buttonElement && buttonElement.contains(e.target as Node);

    if (!isFocusInNav && !isFocusOnButton) {
      props.setMobileNavOpen(false);
    }
  }

  function handleEscapeKeyPress(e: KeyboardEvent) {
    // Early return if not escape key
    if (e.key !== 'Escape') {
      return;
    }

    // If focus is inside the nav, focus button before closing
    const navElement = navRef.current as HTMLElement | null;
    if (navElement && navElement.contains(e.target as Node)) {
      const buttonElement = props.buttonRef.current as HTMLElement | null;
      if (buttonElement) {
        buttonElement.setAttribute('aria-expanded', 'false');
        buttonElement.focus();
      }
    }

    props.setMobileNavOpen(false);
  }

  const handleNavKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!props.mobileNavOpen || e.key !== 'Tab' || e.shiftKey) return;

    const focusableElements = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    ).filter((element) => element.tabIndex >= 0 && element.getAttribute('aria-hidden') !== 'true');
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (document.activeElement === lastFocusableElement) {
      e.preventDefault();
      props.buttonRef.current?.focus();
    }
  };

  // Handle accordion toggle - close others when one opens
  const handleAccordionToggle = (index: number) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index);
  };

  const handleCloseMenu = () => {
    props.setMobileNavOpen(false);
    setOpenSubMenuEvent();
    setOpenAccordionIndex(null);
  };

  useOnRouteChange(handleCloseMenu);

  return (
    <nav
      id={'nav'}
      ref={navRef}
      onKeyDown={handleNavKeyDown}
      className={props.mobileNavOpen ? styles.openDrawer : styles.closedDrawer}
      aria-label={getDictionaryValue('Navigation.Header.MainNavigation') || 'header'}
    >
      <div className={styles.megaNavDrawer}>
        {/* Hide primary links (via CSS, not unmount) when submenu is open, so accordion
            state, refs, and per-item hooks like useIsTablet survive the round trip. */}
        <ul
          className={clsx(
            styles.headerPrimaryLinkBarList,
            openSubMenu && isTablet && styles.hidden
          )}
          id="header-primary-link-bar-list"
        >
          {props?.primaryLinkBar?.map((item, index) => (
            <HeaderMegaMenuContainer
              key={index}
              isMobileMegaNavOpen={props.mobileNavOpen}
              primaryLinkBar={item}
              isOpen={openAccordionIndex === index}
              onToggle={() => handleAccordionToggle(index)}
              openSubMenu={openSubMenu}
              setOpenSubMenu={setOpenSubMenuEvent}
              registerSubmenuTrigger={registerSubmenuTrigger}
              selectedSectionLink={selectedSectionLink}
              firstInteractiveElementRef={index === 0 ? firstInteractiveElementRef : undefined}
              primaryLinkBarIndex={index}
              linkContext={props.linkContext}
            />
          ))}
        </ul>

        {/* Submenu rendered parallel to primaryLinkBarList (For mobile) */}
        {openSubMenu &&
          props.mobileNavOpen &&
          selectedSectionLink &&
          isTablet &&
          (() => {
            // Find the index of the primary link bar that contains the selected section link
            const primaryLinkBarIndex = props.primaryLinkBar.findIndex((primaryLinkBar) =>
              primaryLinkBar.sectionLinks?.some(
                (sectionLink) =>
                  sectionLink.sectionLinkLabel === selectedSectionLink.sectionLinkLabel
              )
            );
            // Find the index of the section link within the primary link bar
            const sectionLinkIndex =
              props.primaryLinkBar[primaryLinkBarIndex]?.sectionLinks?.findIndex(
                (sectionLink) =>
                  sectionLink.sectionLinkLabel === selectedSectionLink.sectionLinkLabel
              ) || 0;
            return (
              <HeaderSubMenu
                sectionLink={selectedSectionLink}
                onBack={() => {
                  setOpenSubMenuEvent(undefined, undefined, true);
                }}
                submenuId={generateSubmenuId(
                  selectedSectionLink,
                  primaryLinkBarIndex,
                  sectionLinkIndex
                )}
                linkContext={props.linkContext}
                analyticsHeader={props.primaryLinkBar[primaryLinkBarIndex]?.primaryLinkLabel}
              />
            );
          })()}
      </div>
    </nav>
  );
};
